import React, {
  useState, useEffect, useContext, useRef
} from 'react';
import PropTypes from 'prop-types';
import { withStyles, withTheme } from '@glu/theming';
import { withInputAPI, FormContext } from '@glu/validation-react';
import locale from '@glu/locale';
import moment from 'moment';
import SchedulerContext from './SchedulerContext';
import Start from './Start';
import End, { SIMPLE, NONE } from './End';
import {
  getDefaultType, generateRecurrenceObject, WEEKLY
} from './utils/recurrence';
import Recurrence from './recurrence/Recurrence';
import {
  types
} from './recurrence/Recurrence.reducer';
import { getSchedulingValue } from './utils/date';
import './themeDefaults';
import { END_DATE, FURTHER_NOTICE, OCCURRENCE } from './utils/end';

const styles = {
  scheduler: {}
};

const Scheduler = ({
  classes,
  className,
  dataQa,
  htmlId,
  name,
  starts,
  ends,
  endsComplexOptions,
  dateFormat,
  disabled,
  getValueOnStart,
  onChange,
  minutes,
  hourly,
  daily,
  weekly,
  monthly,
  semiMonthly,
  yearly,
  type,
  timePicker24Hour,
  minTime,
  maxTime,
  minuteStep,
  exactMinuteStep,
  hideLastDayInDays,
  DatePickerProps,
  ...dates
}) => {
  const defaultType = getDefaultType({
    minutes, hourly, daily, weekly, monthly, semiMonthly, yearly
  });
  const [startValue, setStartValue] = useState(getSchedulingValue(dates.startValue));
  const [endValue, setEndValue] = useState(getSchedulingValue(dates.endValue));
  const [recurrence, setRecurrence] = useState(dates.recurrence);
  const [recurrenceType, setRecurrenceType] = useState(type || defaultType);
  const [startError, setStartError] = useState(undefined);
  const [endError, setEndError] = useState(undefined);
  const [recurrenceError, setRecurrenceError] = useState(undefined);
  const onChangeRef = useRef(onChange);
  const prevValue = useRef();
  const recurrenceConfig = {
    minutes,
    hourly,
    daily,
    weekly,
    monthly,
    semiMonthly,
    yearly,
    recurrenceType,
    setRecurrenceType,
    minTime,
    maxTime,
    minuteStep,
    exactMinuteStep,
    hideLastDayInDays,
    getValueOnStart,
    recurrenceError,
    onChange: setRecurrence,
    ...recurrence
  };

  useEffect(() => {
    const currentValue = {
      name,
      value: {
        startValue: moment.isMoment(startValue) ? startValue.toDate() : startValue,
        endValue: moment.isMoment(endValue) ? endValue.toDate() : endValue,
        type: recurrenceType,
        recurrence: generateRecurrenceObject(recurrence, recurrenceType)
      }
    };
    onChangeRef.current({ target: currentValue });
    prevValue.current = currentValue;
  }, [name, onChangeRef, startValue, endValue, recurrence, recurrenceType]);

  const formValue = useContext(FormContext);

  useEffect(() => {
    const validate = () => {
      const errors = [];
      const { recurrence: curRecurrence, type: curType } = prevValue.current.value;
      if (starts && !startValue) {
        setStartError(locale.get('validator.required'));
        errors.push('Start date required');
      } else {
        setStartError(undefined);
      }
      if (!endValue && ends === SIMPLE) {
        setEndError(locale.get('validator.required'));
        errors.push('End date required');
      } else {
        setEndError(undefined);
      }
      if (curType === WEEKLY
        && !Number.isNaN(weekly.minDays)
        && ((curRecurrence.onN && curRecurrence.onN.length) || 0) < weekly.minDays) {
        setRecurrenceError(locale.get('schedulerReact.weeklyMin', weekly.minDays));
        errors.push(locale.get('schedulerReact.weeklyMin', weekly.minDays));
      } else {
        setRecurrenceError(undefined);
      }
      return {
        data: prevValue.current,
        errors,
        isValid: errors.length === 0
      };
    };
    return formValue && formValue.registerSelfValidatingChild(name, validate);
  }, [formValue, name, startValue, endValue, ends, starts, weekly, type]);

  return (
    <SchedulerContext.Provider value={{
      disabled,
      dataQa,
      htmlId,
      dateFormat,
      timePicker24Hour
    }}
    >
      <div data-qa={dataQa ? `${dataQa}` : `scheduler-${htmlId}`} className={`${classes.scheduler} ${className}`}>
        { starts && (
          <Start
            startValue={startValue}
            setStartValue={setStartValue}
            endValue={endValue}
            DatePickerProps={DatePickerProps}
            error={startError}
          />
        )}
        <Recurrence {...recurrenceConfig} />
        { ends !== NONE && (
          <End
            endValue={endValue}
            endsComplexOptions={endsComplexOptions}
            setEndValue={setEndValue}
            startValue={startValue}
            type={ends}
            DatePickerProps={DatePickerProps}
            error={endError}
          />
        )}
      </div>
    </SchedulerContext.Provider>
  );
};

Scheduler.propTypes = {
  /** Classes provided by the theme */
  classes: PropTypes.objectOf(PropTypes.string).isRequired,

  /** Class passed to parent element for extra decoration. */
  className: PropTypes.string,

  /** String given to parent elements data-qa html attribute for easy targeting */
  dataQa: PropTypes.string,

  /** Disable the dropdown entirely */
  disabled: PropTypes.bool,

  /** String assigned to HTML id attribute of component */
  htmlId: PropTypes.string,

  /** String passed to html name attribute of text input */
  name: PropTypes.string,

  /** String containing date format for selected dates */
  dateFormat: PropTypes.string,

  /** Set start value within module */
  starts: PropTypes.bool,

  /** Start value */
  startValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
    PropTypes.object]),

  /** Set end value within module */
  ends: PropTypes.oneOf(['NONE', 'SIMPLE', 'COMPLEX']),

  /** Configuration Options to allow in Complex End Mode,
   * after x occurrences,
   * on a specified end date,
   * until further notice */
  endsComplexOptions: PropTypes.arrayOf(
    PropTypes.oneOf([OCCURRENCE, END_DATE, FURTHER_NOTICE])
  ),

  /** End Value */
  endValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
    PropTypes.object]),

  /** Display recurrence by minute */
  minutes: PropTypes.bool,

  /** Hide 'last' day in days dropdown */
  hideLastDayInDays: PropTypes.bool,

  /** Display recurrence by hour */
  hourly: PropTypes.bool,

  /** Minimum time value of input */
  minTime: PropTypes.string,

  /** Maximum time value of input */
  maxTime: PropTypes.string,

  /** Value for increment minutes with */
  minuteStep: PropTypes.number,

  /** Avoid to enter minutes manually. The user can change the minutes only by +- buttons */
  exactMinuteStep: PropTypes.bool,

  /** Get scheduler state on initial render */
  getValueOnStart: PropTypes.bool,

  /** Display recurrence by day */
  daily: PropTypes.shape({
    enabled: PropTypes.bool,
    canSetTime: PropTypes.bool
  }),

  /** Display recurrence by week */
  weekly: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      enabled: PropTypes.bool,
      canSetTime: PropTypes.bool,
      allowWeekend: PropTypes.bool,
      daysAvailable: PropTypes.arrayOf(PropTypes.string),
      minDays: PropTypes.number
    })
  ]),

  /** Display recurrence by month */
  monthly: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      enabled: PropTypes.bool,
      canSetTime: PropTypes.bool,
      allowWeekend: PropTypes.bool,
      multipleDays: PropTypes.bool
    })
  ]),

  /** Display recurrence for twice a month */
  semiMonthly: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      enabled: PropTypes.bool,
      canSetTime: PropTypes.bool
    })
  ]),

  /** Display recurrence by year */
  yearly: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      enabled: PropTypes.bool,
      canSetTime: PropTypes.bool,
      multipleDays: PropTypes.bool
    })
  ]),

  /** Object containing recurrence data */
  recurrence: PropTypes.shape({
    onN: PropTypes.instanceOf(Array),
    intervalType: PropTypes.oneOf([
      types.MINUTES, types.HOURLY, types.DAILY, types.WEEKLY, types.MONTHLY, types.YEARLY
    ])
  }),

  /** Type of recurrence */
  type: PropTypes.oneOf([
    types.MINUTES, types.HOURLY, types.DAILY, types.WEEKLY, types.MONTHLY, types.YEARLY
  ]),

  /** Display 24 hour military style time. Also hides am/pm selector * */
  timePicker24Hour: PropTypes.bool,

  /** Function to change the data after a value changes */
  onChange: PropTypes.func,

  /** Passthrough props for datepicker */
  DatePickerProps: PropTypes.objectOf(PropTypes.shape({}))
};

Scheduler.defaultProps = {
  className: '',
  endValue: null,
  dataQa: '',
  dateFormat: 'D MMM YYYY',
  disabled: false,
  ends: NONE,
  endsComplexOptions: [FURTHER_NOTICE, OCCURRENCE, END_DATE],
  getValueOnStart: false,
  htmlId: '',
  name: '',
  starts: false,
  startValue: null,
  onChange: /* istanbul ignore next */ () => {},
  minutes: false,
  hourly: false,
  daily: { enabled: true },
  weekly: false,
  monthly: false,
  semiMonthly: false,
  yearly: false,
  minTime: undefined,
  maxTime: undefined,
  minuteStep: undefined,
  exactMinuteStep: false,
  hideLastDayInDays: false,
  type: null,
  recurrence: {},
  timePicker24Hour: false,
  DatePickerProps: {}
};

export const BareScheduler = Scheduler;
export const ValidatedBareScheduler = withInputAPI(Scheduler);
const StyledScheduler = withTheme(withStyles(styles)(Scheduler));
StyledScheduler.displayName = 'Scheduler';
export const ValidatedScheduler = withInputAPI(StyledScheduler);
ValidatedScheduler.displayName = 'Scheduler';
export default ValidatedScheduler;
